"use client";

/**
 * ReflectAiSuggestion — warm-gradient card that surfaces ONE recommendation
 * at a time on the Reflect page. Pager is reserved for the future when the
 * backend can return N alternatives; today it's hidden when there's only
 * one suggestion.
 *
 * State machine:
 *   default → user sees headline + because + impact + Try this / Skip
 *   rejecting → reject-reason chips. Picking one posts feedback + advances
 *               to next suggestion (or returns to default if at end).
 *   accepted → confirmation banner.
 *
 * Wires Try this → existing pendingCaptureIntent flow (navigate to
 * "present" with prefill); Skip → POST /api/recommendations/{id}/dismiss.
 * Same contract as the legacy `SuggestionCard` so the recommendation
 * pipeline doesn't need to change.
 */

import { useEffect, useMemo, useState } from "react";
import styles from "./reflect-page.module.css";

/** What "Try this" should DO for a given rec. Each value implies a
 *  different button label so the user knows what's about to happen. */
export type ReflectAiAction =
  | "schedule_block"
  | "add_task"
  | "open_goal"
  | "save_goal"
  | "noted";

export interface ReflectAiSuggestionItem {
  id: string;
  /** Citation block: which area / priority / counts the rec is grounded in. */
  cite: {
    area?: string | null;
    priority?: number | null;
    scheduled?: string | null;
    target?: string | null;
  };
  /** Imperative recommendation, ≤14 words ideally. */
  headline: string;
  /** "Because: …" body. Markdown-ish: **bold** is parsed inline. */
  because: string;
  /** Optional impact preview (before / after). */
  impact?: {
    before: { lbl: string; v: string };
    after: { lbl: string; v: string; delta?: boolean };
  };
  /** When set, the rec is anchored to an existing goal — "Try this"
   *  attaches the task to it instead of spawning a duplicate goal. */
  goal_id?: string | null;
  goal_title?: string | null;
  /** What the accept button should actually do. */
  action: ReflectAiAction;
  /** The verb shown on the primary button — derives from `action`. */
  action_label: string;
  /** The text shown after acceptance — derives from `action`. */
  accepted_label: string;
  /** Pre-derived task title used by the host's onTry handler. Empty
   *  for actions that don't create a task (open_goal / save_goal /
   *  noted). Component itself never reads this — pure handoff field. */
  task_title?: string;
  /** Resolved start moment for a `schedule_block` action — the actual
   *  Date the block lands on, after honest weekend/today/weekday
   *  resolution. Used to render a real date in the accepted banner and
   *  to compute `reveal_kind`. Accepts ISO string or Date for caller
   *  flexibility. */
  scheduled_start?: Date | string | null;
  /** Where the reveal button in the accepted banner should jump.
   *  Computed by the host based on scheduled_start relative to "now":
   *    "today" → block is today → jump to Today tab
   *    "plan"  → block is in the future → jump to Plan tab
   *    "goal"  → fallback (no date known, or non-schedule action) →
   *              jump to the goal's Goals card (legacy behaviour) */
  reveal_kind?: "today" | "plan" | "goal";
}

export interface ReflectAiSuggestionProps {
  suggestions: ReflectAiSuggestionItem[];
  loading?: boolean;
  onTry: (s: ReflectAiSuggestionItem) => void | Promise<void>;
  onSkip: (s: ReflectAiSuggestionItem, reason: string) => void | Promise<void>;
  /** Optional: if the accepted rec has a goal_id, this is fired when the
   *  confirmation banner's "View goal" button is clicked. */
  onViewGoal?: (goalId: string) => void;
  /** Optional: jump to Today (created task lands there). */
  onViewToday?: () => void;
  /** Optional: jump to Plan tab (future-dated schedule block lands there). */
  onViewPlan?: () => void;
}

const REJECT_REASONS = [
  "Wrong focus",
  "Bad timing",
  "Already doing it",
  "Too small",
  "Other",
] as const;

function SparkleIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 2l1.8 5.6L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.4L12 2z" />
    </svg>
  );
}

/** Inline-render `**bold**` markdown without bringing in a parser. */
function renderBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export default function ReflectAiSuggestion({
  suggestions,
  loading = false,
  onTry,
  onSkip,
  onViewGoal,
  onViewToday,
  onViewPlan,
}: ReflectAiSuggestionProps) {
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState<"default" | "rejecting" | "accepted">(
    "default",
  );

  // Reset to default whenever the active suggestion changes.
  useEffect(() => {
    setState("default");
  }, [idx]);

  // Clamp idx if the suggestions array shrinks underneath us.
  const safeIdx = useMemo(
    () => Math.min(idx, Math.max(0, suggestions.length - 1)),
    [idx, suggestions.length],
  );
  const current: ReflectAiSuggestionItem | null =
    suggestions.length > 0 ? suggestions[safeIdx] : null;

  if (loading && !current) {
    return (
      <section className={styles["rf-ai"]} data-testid="reflect-ai-loading">
        <div className={styles["rf-ai-tag"]}>
          <SparkleIcon /> Worth considering
        </div>
        <p className={styles["rf-ai-empty"]}>
          Looking through the week for the most useful nudge…
        </p>
      </section>
    );
  }

  if (!current) {
    return (
      <section className={styles["rf-ai"]} data-testid="reflect-ai-empty">
        <div className={styles["rf-ai-tag"]}>
          <SparkleIcon /> Worth considering
        </div>
        <p className={styles["rf-ai-empty"]}>
          Nothing to suggest right now. Add a few schedule blocks and we&apos;ll
          surface the most useful one next week.
        </p>
      </section>
    );
  }

  if (state === "accepted") {
    return (
      <section className={styles["rf-ai"]} data-testid="reflect-ai-card">
        <div className={styles["rf-ai-tag"]}>
          <SparkleIcon /> Worth considering
        </div>
        <div className={styles["rf-ai-confirmed"]}>
          {current.accepted_label}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            marginTop: 8,
            position: "relative",
            zIndex: 1,
            flexWrap: "wrap",
          }}
        >
          {/*
            Reveal target priority (dogfood B4/B5 fix):
              1. schedule_block + reveal_kind === "today" → View in Today
              2. schedule_block + reveal_kind === "plan"  → View in Plan
              3. add_task (legacy)                        → View in Today
              4. has goal_id (default fallback)           → View {goal}
            Multiple buttons may render only when the action is
            schedule_block AND reveal_kind === "today" — in that case
            the goal-reveal button is suppressed because the user wants
            to see the BLOCK they just created, not the goal it's
            anchored to (which doesn't render ScheduleBlocks).
          */}
          {current.action === "schedule_block" &&
          current.reveal_kind === "today" &&
          onViewToday ? (
            <button
              type="button"
              onClick={onViewToday}
              className={styles["rf-ai-skip"]}
              style={{ color: "var(--bl-forest)" }}
              data-testid="reflect-ai-view-today"
            >
              View in Today →
            </button>
          ) : current.action === "schedule_block" &&
            current.reveal_kind === "plan" &&
            onViewPlan ? (
            <button
              type="button"
              onClick={onViewPlan}
              className={styles["rf-ai-skip"]}
              style={{ color: "var(--bl-forest)" }}
              data-testid="reflect-ai-view-plan"
            >
              View in Plan →
            </button>
          ) : current.action === "add_task" && onViewToday ? (
            <button
              type="button"
              onClick={onViewToday}
              className={styles["rf-ai-skip"]}
              style={{ color: "var(--bl-forest)" }}
              data-testid="reflect-ai-view-today"
            >
              View in Today →
            </button>
          ) : current.goal_id && onViewGoal ? (
            <button
              type="button"
              onClick={() => onViewGoal(current.goal_id as string)}
              className={styles["rf-ai-skip"]}
              style={{ color: "var(--bl-forest)" }}
              data-testid="reflect-ai-view-goal"
            >
              View {current.goal_title ?? "this goal"} →
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  const handleSkipReason = async (reason: string) => {
    try {
      await onSkip(current, reason);
    } finally {
      // Advance to the next suggestion if we have one; otherwise drop back
      // to default state so the user isn't stuck in the reject panel.
      if (safeIdx < suggestions.length - 1) {
        setIdx(safeIdx + 1);
      } else {
        setState("default");
      }
    }
  };

  return (
    <section className={styles["rf-ai"]} data-testid="reflect-ai-card">
      <div className={styles["rf-ai-tag"]}>
        <SparkleIcon /> Worth considering
      </div>
      <h3 className={styles["rf-ai-headline"]}>{current.headline}</h3>
      <div className={styles["rf-ai-because"]}>
        <strong>Because:</strong> {renderBold(current.because)}
      </div>
      {current.impact && (
        <div className={styles["rf-ai-impact"]}>
          <div className={styles["rf-ai-impact-side"]}>
            <span className={styles["lbl"]}>{current.impact.before.lbl}</span>
            <span className={styles["v"]}>{current.impact.before.v}</span>
          </div>
          <div className={styles["rf-ai-impact-arrow"]} aria-hidden>
            →
          </div>
          <div className={styles["rf-ai-impact-side"]}>
            <span className={styles["lbl"]}>{current.impact.after.lbl}</span>
            <span
              className={`${styles["v"]} ${
                current.impact.after.delta ? styles["delta"] : ""
              }`}
            >
              {current.impact.after.v}
            </span>
          </div>
        </div>
      )}
      <div className={styles["rf-ai-actions"]}>
        <button
          type="button"
          className={styles["rf-ai-try"]}
          onClick={async () => {
            // Wait for onTry to settle before flipping the UI.
            // If onTry throws (network down, server 500, host's own
            // validation bailed) we stay in default state so the user
            // can retry rather than seeing a false "✓ Added" banner
            // (UAT P15, 2026-05-07).
            try {
              await onTry(current);
              setState("accepted");
            } catch {
              // Leave state alone; the next render will reflect retry.
            }
          }}
          data-testid="reflect-ai-try"
        >
          {current.action_label}
        </button>
        <button
          type="button"
          className={styles["rf-ai-skip"]}
          onClick={() => setState("rejecting")}
          data-testid="reflect-ai-skip"
        >
          Skip — not right
        </button>
        {suggestions.length > 1 && (
          <div className={styles["rf-ai-pager"]}>
            <button
              type="button"
              onClick={() => setIdx(Math.max(0, safeIdx - 1))}
              disabled={safeIdx === 0}
              aria-label="Previous suggestion"
            >
              ‹
            </button>
            <span>
              {safeIdx + 1} / {suggestions.length}
            </span>
            <button
              type="button"
              onClick={() =>
                setIdx(Math.min(suggestions.length - 1, safeIdx + 1))
              }
              disabled={safeIdx === suggestions.length - 1}
              aria-label="Next suggestion"
            >
              ›
            </button>
          </div>
        )}
      </div>

      {state === "rejecting" && (
        <div className={styles["rf-ai-reject"]}>
          <div className={styles["rf-ai-reject-q"]}>
            Help us learn — why isn&apos;t this right?
          </div>
          <div className={styles["rf-ai-reject-opts"]}>
            {REJECT_REASONS.map((r) => (
              <button
                type="button"
                key={r}
                className={styles["rf-ai-reject-opt"]}
                onClick={() => void handleSkipReason(r)}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
